/**
 * VideoRoom Component
 *
 * Main video call container using Daily.co React SDK.
 * Handles connecting to the room, displaying participants, and call controls.
 *
 * Branded with Nimbbus Providers colors: Teal (#4A8A9A), Soft Teal (#4A8A9A),
 * Sky Blue (#87B2E5), Gold (#CC964E)
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  DailyProvider,
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useDailyEvent,
  useDevices,
  DailyVideo,
  DailyAudio,
  useCallObject,
} from '@daily-co/daily-react';
import DailyIframe from '@daily-co/daily-js';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Monitor,
  MonitorUp,
  Users,
  Clock,
  Loader2,
  AlertCircle,
  FileText,
  ClipboardCheck,
  Settings,
  RefreshCw,
  MessageCircle,
  Send,
  X,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import TranscriptPanel from './TranscriptPanel';
import TranscriptSOAPEditor from './TranscriptSOAPEditor';
import LiveSummaryPanel from './LiveSummaryPanel';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import ConnectionQualityIndicator from './ConnectionQualityIndicator';
import transcriptService from '@/services/transcriptService';

// Transcript line type
export interface TranscriptLine {
  id: string;
  speaker: string;
  speakerName: string;
  speakerId?: string;
  text: string;
  timestamp: string;
  isFinal: boolean;
}

// Participant info for speaker identification
interface ParticipantInfo {
  sessionId: string;
  name: string;
  isProvider: boolean;
}

// Nimbbus Providers brand colors
const BRAND_COLORS = {
  teal: '#4A8A9A',
  softTeal: '#4A8A9A',
  skyBlue: '#87B2E5',
  gold: '#CC964E',
};

interface VideoRoomProps {
  roomUrl: string;
  token: string;
  displayName: string;
  isProvider?: boolean;
  appointmentInfo?: {
    patientName?: string;
    providerName?: string;
    startTime?: string;
    duration?: number;
  };
  // Required for SOAP note generation (provider only)
  patientId?: string;
  clinicId?: string;
  appointmentId?: string;
  encounterId?: string;
  // Pre-join device state - if set, join with these settings
  startVideoOff?: boolean;
  startAudioOff?: boolean;
  onLeave?: () => void;
  onError?: (error: Error) => void;
}

// Inner component that uses Daily hooks (must be inside DailyProvider)
const VideoRoomInner: React.FC<VideoRoomProps> = ({
  roomUrl,
  token,
  displayName,
  isProvider = false,
  appointmentInfo,
  patientId,
  clinicId,
  appointmentId,
  encounterId,
  startVideoOff = false,
  startAudioOff = false,
  onLeave,
  onError,
}) => {
  const daily = useDaily();
  const localSessionId = useLocalSessionId();
  const participantIds = useParticipantIds({ filter: 'remote' });
  const { cameras, microphones, speakers } = useDevices();
  const { toast } = useToast();

  const [isJoining, setIsJoining] = useState(true);
  // Initialize audio/video state based on pre-join settings
  const [isAudioOn, setIsAudioOn] = useState(!startAudioOff);
  const [isVideoOn, setIsVideoOn] = useState(!startVideoOff);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenSharingSessionId, setScreenSharingSessionId] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isCallJoined, setIsCallJoined] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Transcription state
  const [transcriptLines, setTranscriptLines] = useState<TranscriptLine[]>([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);

  // Participant tracking for speaker identification
  // Maps Daily session IDs to participant info (name, role)
  const [participantMap, setParticipantMap] = useState<Record<string, ParticipantInfo>>({});

  // Track video state for each participant
  const [participantVideoState, setParticipantVideoState] = useState<Record<string, boolean>>({});

  // SOAP Editor state
  const [showSOAPEditor, setShowSOAPEditor] = useState(false);
  const [savedTranscriptId, setSavedTranscriptId] = useState<string | undefined>();

  // Side panel tab state
  const [activeSideTab, setActiveSideTab] = useState<'summary' | 'transcript'>('transcript');
  
  // Separate chat panel state
  const [showChatPanel, setShowChatPanel] = useState(false);

  // Check for existing transcript when component mounts (if appointmentId is available)
  useEffect(() => {
    const checkExistingTranscript = async () => {
      if (isProvider && clinicId && appointmentId && !savedTranscriptId) {
        try {
          const transcript = await transcriptService.getAppointmentTranscript(clinicId, appointmentId);
          if (transcript) {
            setSavedTranscriptId(transcript.id);
            console.log('[VideoRoom] Found existing transcript:', transcript.id);
          }
        } catch (error) {
          // Transcript doesn't exist yet, that's fine
          console.log('[VideoRoom] No existing transcript found');
        }
      }
    };

    checkExistingTranscript();
  }, [isProvider, clinicId, appointmentId, savedTranscriptId]);

  // Device settings panel state
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);

  // Chat state
  interface ChatMessage {
    id: string;
    sender: string;
    senderName: string;
    text: string;
    timestamp: Date;
    isLocal: boolean;
  }
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Connection quality state
  const [connectionQuality, setConnectionQuality] = useState<'excellent' | 'good' | 'fair' | 'poor' | 'unknown'>('unknown');

  // Swapped view state - when true, local video is shown as main view
  const [isViewSwapped, setIsViewSwapped] = useState(false);

  // Reconnection state
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Join the room when component mounts
  useEffect(() => {
    if (!daily) return;

    const joinRoom = async () => {
      try {
        setIsJoining(true);
        setJoinError(null);

        await daily.join({
          url: roomUrl,
          token: token,
          userName: displayName,
          startVideoOff: startVideoOff,
          startAudioOff: startAudioOff,
        });

        // Track local participant for speaker identification
        const localParticipant = daily.participants().local;
        if (localParticipant) {
          setParticipantMap(prev => ({
            ...prev,
            [localParticipant.session_id]: {
              sessionId: localParticipant.session_id,
              name: displayName,
              isProvider: isProvider
            }
          }));
          console.log('[VideoRoom] Tracked local participant:', localParticipant.session_id, '-> ', displayName, isProvider ? '(Provider)' : '(Patient)');
        }

        setIsJoining(false);
        setIsCallJoined(true);
        
        // Check for existing participants after a short delay to ensure Daily.co state is updated
        // This handles cases where someone joined before us
        setTimeout(() => {
          try {
            const allParticipants = daily.participants();
            const localSessionId = localParticipant?.session_id;
            
            if (!allParticipants || !localSessionId) return;
            
            // daily.participants() returns an object with session_id keys
            // Filter out local participant and get remote participants
            const remoteParticipants = Object.values(allParticipants).filter(
              p => p && p.session_id && p.session_id !== localSessionId
            );
            
            if (remoteParticipants.length > 0) {
              console.log('[VideoRoom] Found', remoteParticipants.length, 'existing participant(s) in room');
              remoteParticipants.forEach(participant => {
                const participantName = participant.user_name || 'Unknown';
                const participantIsProvider = !isProvider; // Remote participant is opposite role
                
                setParticipantMap(prev => {
                  // Don't add if already exists
                  if (prev[participant.session_id]) return prev;
                  
                  return {
                    ...prev,
                    [participant.session_id]: {
                      sessionId: participant.session_id,
                      name: participantName,
                      isProvider: participantIsProvider,
                    },
                  };
                });
                
                console.log(
                  '[VideoRoom] Tracked existing participant:',
                  participant.session_id,
                  '->',
                  participantName,
                  participantIsProvider ? '(Provider)' : '(Patient)'
                );
                
                toast({
                  title: 'Participant already in call',
                  description: `${participantName} is already in the call`,
                });
              });
            }
          } catch (error) {
            console.warn('[VideoRoom] Error checking for existing participants:', error);
          }
        }, 1000);
        toast({
          title: 'Connected',
          description: 'You have joined the video call',
        });

        // Start transcription for providers after join is complete
        if (isProvider) {
          // Wait a bit for the join to fully complete before starting transcription
          setTimeout(async () => {
            try {
              const meetingState = daily.meetingState();
              if (meetingState === 'joined-meeting' || meetingState === 'joined') {
                await daily.startTranscription();
                setIsTranscribing(true);
                console.log('[VideoRoom] Transcription started');
                toast({
                  title: 'Transcription Active',
                  description: 'Call is being transcribed for clinical notes',
                });
              }
            } catch (transcriptError) {
              console.warn('[VideoRoom] Failed to start transcription:', transcriptError);
              setTranscriptionError(
                transcriptError instanceof Error
                  ? transcriptError.message
                  : 'Failed to start transcription'
              );
              // Don't fail the call for transcription errors
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Failed to join room:', error);
        setJoinError(error instanceof Error ? error.message : 'Failed to join call');
        setIsJoining(false);
        onError?.(error instanceof Error ? error : new Error('Failed to join call'));
      }
    };

    joinRoom();

    return () => {
      // Only room owner (provider) can stop transcription
      // Check if daily exists and we're actually in a meeting before stopping
      if (daily && isTranscribing && isProvider && isCallJoined) {
        try {
          const meetingState = daily.meetingState();
          if (meetingState === 'joined-meeting' || meetingState === 'joined') {
            daily.stopTranscription().catch(console.error);
          }
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      setIsCallJoined(false);
      if (daily) {
        try {
          daily.leave();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, [daily, roomUrl, token, displayName, isProvider, startVideoOff, startAudioOff]);

  // Track call duration
  useEffect(() => {
    if (isJoining) return;

    const interval = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isJoining]);

  // Handle participant events
  // Listen for joined-meeting event to check for existing participants
  useDailyEvent(
    'joined-meeting',
    useCallback(
      () => {
        if (!daily || !localSessionId) return;
        
        console.log('[VideoRoom] joined-meeting event fired, checking for existing participants');
        
        // Check for existing participants when we join - use multiple attempts with delays
        const checkParticipants = (attempt = 1, maxAttempts = 3) => {
          setTimeout(() => {
            try {
              const allParticipants = daily.participants();
              
              if (!allParticipants) {
                if (attempt < maxAttempts) {
                  checkParticipants(attempt + 1, maxAttempts);
                }
                return;
              }
              
              const remoteParticipants = Object.values(allParticipants).filter(
                p => p && p.session_id && p.session_id !== localSessionId
              );
              
              console.log(`[VideoRoom] Check attempt ${attempt}: Found ${remoteParticipants.length} remote participant(s)`);
              
              if (remoteParticipants.length > 0) {
                remoteParticipants.forEach(participant => {
                  const participantName = participant.user_name || 'Unknown';
                  const participantIsProvider = !isProvider;
                  
                  setParticipantMap(prev => {
                    if (prev[participant.session_id]) return prev;
                    
                    console.log('[VideoRoom] Adding existing participant to map:', participant.session_id, participantName);
                    
                    return {
                      ...prev,
                      [participant.session_id]: {
                        sessionId: participant.session_id,
                        name: participantName,
                        isProvider: participantIsProvider,
                      },
                    };
                  });
                });
              } else if (attempt < maxAttempts) {
                // Try again if no participants found yet
                checkParticipants(attempt + 1, maxAttempts);
              }
            } catch (error) {
              console.warn('[VideoRoom] Error checking participants in joined-meeting:', error);
              if (attempt < maxAttempts) {
                checkParticipants(attempt + 1, maxAttempts);
              }
            }
          }, attempt * 500); // Increasing delay: 500ms, 1000ms, 1500ms
        };
        
        checkParticipants();
      },
      [daily, localSessionId, isProvider]
    )
  );

  useDailyEvent(
    'participant-joined',
    useCallback(
      event => {
        if (event?.participant) {
          const participant = event.participant;
          const participantName = participant.user_name || 'Unknown';

          // Determine if the joining participant is a provider or patient
          // Remote participants are assumed to be the opposite role of the local user
          // If we're the provider, the person joining is likely the patient (and vice versa)
          const participantIsProvider = !isProvider;

          // Track this participant for speaker identification
          setParticipantMap(prev => {
            // Don't add if already exists
            if (prev[participant.session_id]) return prev;
            
            return {
              ...prev,
              [participant.session_id]: {
                sessionId: participant.session_id,
                name: participantName,
                isProvider: participantIsProvider,
              },
            };
          });

          console.log(
            '[VideoRoom] Tracked remote participant:',
            participant.session_id,
            '->',
            participantName,
            participantIsProvider ? '(Provider)' : '(Patient)'
          );

          toast({
            title: 'Participant joined',
            description: `${participantName} has joined the call`,
          });
        }
      },
      [toast, isProvider]
    )
  );

  useDailyEvent(
    'participant-left',
    useCallback(
      event => {
        if (event?.participant) {
          toast({
            title: 'Participant left',
            description: `${event.participant.user_name || 'Someone'} has left the call`,
          });
        }
      },
      [toast]
    )
  );

  useDailyEvent(
    'error',
    useCallback(
      event => {
        console.error('Daily error:', event);
        toast({
          title: 'Call Error',
          description: event?.errorMsg || 'An error occurred',
          variant: 'destructive',
        });
      },
      [toast]
    )
  );

  // Network quality tracking
  useDailyEvent(
    'network-quality-change',
    useCallback(
      (event: any) => {
        if (event?.threshold) {
          // Daily provides threshold: 'very-low', 'low', 'good', 'excellent'
          const qualityMap: Record<string, 'excellent' | 'good' | 'fair' | 'poor'> = {
            'excellent': 'excellent',
            'good': 'good',
            'low': 'fair',
            'very-low': 'poor',
          };
          setConnectionQuality(qualityMap[event.threshold] || 'unknown');
        }
      },
      []
    )
  );

  // Network connection events for reconnection handling
  useDailyEvent(
    'network-connection',
    useCallback(
      (event: any) => {
        if (event?.event === 'interrupted') {
          console.log('[VideoRoom] Network interrupted, attempting reconnection...');
          setIsReconnecting(true);
          toast({
            title: 'Connection Interrupted',
            description: 'Attempting to reconnect...',
          });
        } else if (event?.event === 'connected') {
          console.log('[VideoRoom] Network reconnected');
          setIsReconnecting(false);
          reconnectAttemptsRef.current = 0;
          toast({
            title: 'Reconnected',
            description: 'Your connection has been restored',
          });
        }
      },
      [toast]
    )
  );

  // Handle call state changes for reconnection
  useDailyEvent(
    'left-meeting',
    useCallback(
      async (event: any) => {
        // If we were disconnected unexpectedly (not user-initiated), try to reconnect
        if (event?.reason === 'network-issues' && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`[VideoRoom] Attempting reconnection (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
          setIsReconnecting(true);
          
          // Wait 2 seconds before trying to rejoin
          setTimeout(async () => {
            if (daily) {
              try {
                await daily.join({
                  url: roomUrl,
                  token: token,
                  userName: displayName,
                  startVideoOff: !isVideoOn,
                  startAudioOff: !isAudioOn,
                });
                setIsReconnecting(false);
                reconnectAttemptsRef.current = 0;
                toast({
                  title: 'Reconnected',
                  description: 'Successfully rejoined the call',
                });
              } catch (error) {
                console.error('[VideoRoom] Reconnection failed:', error);
                if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
                  setIsReconnecting(false);
                  toast({
                    title: 'Reconnection Failed',
                    description: 'Unable to reconnect after multiple attempts. Please rejoin manually.',
                    variant: 'destructive',
                  });
                }
              }
            }
          }, 2000);
        }
      },
      [daily, roomUrl, token, displayName, isVideoOn, isAudioOn, toast]
    )
  );

  // Transcription events
  useDailyEvent(
    'transcription-started',
    useCallback(() => {
      console.log('[VideoRoom] Transcription started event received');
      setIsTranscribing(true);
    }, [])
  );

  useDailyEvent(
    'transcription-stopped',
    useCallback(() => {
      console.log('[VideoRoom] Transcription stopped event received');
      setIsTranscribing(false);
    }, [])
  );

  useDailyEvent(
    'transcription-error',
    useCallback(
      (event: any) => {
        console.error('[VideoRoom] Transcription error:', event);
        setTranscriptionError(event?.errorMsg || 'Transcription error');
        // Only show transcription errors to providers (room owners)
        // Patients don't need to see these errors as transcription is provider-managed
        if (isProvider) {
          toast({
            title: 'Transcription Error',
            description: event?.errorMsg || 'Failed to transcribe',
            variant: 'destructive',
          });
        }
      },
      [toast, isProvider]
    )
  );

  // Handle transcription messages
  useDailyEvent(
    'transcription-message',
    useCallback(
      (event: any) => {
        if (!event) return;

        const participantId = event.participantId || event.session_id || 'unknown';

        // Look up the speaker from our participant map
        const participant = participantMap[participantId];

        // Determine speaker name using priority:
        // 1. Event's user_name (if Daily provides it)
        // 2. Our tracked participant map
        // 3. Fallback based on whether local user is provider
        let speakerName: string;
        if (event.user_name || event.userName) {
          speakerName = event.user_name || event.userName;
        } else if (participant) {
          speakerName = participant.isProvider ? 'Provider' : 'Patient';
        } else {
          // Unknown participant - check if it's the local session
          speakerName =
            participantId === localSessionId
              ? isProvider
                ? 'Provider'
                : 'Patient'
              : isProvider
                ? 'Patient'
                : 'Provider'; // Remote is opposite of local
        }

        const newLine: TranscriptLine = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          speaker: participantId,
          speakerName: speakerName,
          speakerId: participantId,
          text: event.text || '',
          timestamp: new Date().toISOString(),
          isFinal: event.is_final ?? true,
        };

        setTranscriptLines(prev => {
          // If not final, update the last line from same speaker
          if (!newLine.isFinal && prev.length > 0) {
            const lastLine = prev[prev.length - 1];
            if (lastLine.speaker === newLine.speaker && !lastLine.isFinal) {
              return [...prev.slice(0, -1), newLine];
            }
          }
          return [...prev, newLine];
        });
      },
      [participantMap, localSessionId, isProvider]
    )
  );

  // Screen share events - try multiple event names
  useDailyEvent(
    'screen-share-started',
    useCallback(
      (event: any) => {
        console.log('[VideoRoom] Screen share started event received');
        setIsScreenSharing(true);
        // Determine if it's local or remote - check event for session_id
        const sessionId = event?.participant?.session_id || localSessionId;
        setScreenSharingSessionId(sessionId);
      },
      [isScreenSharing, localSessionId]
    )
  );

  useDailyEvent(
    'screen-share-stopped',
    useCallback(
      (event: any) => {
        console.log('[VideoRoom] Screen share stopped event received');
        setIsScreenSharing(false);
        setScreenSharingSessionId(null);
      },
      [isScreenSharing]
    )
  );

  // Alternative: participant-updated event (might fire when screen share changes)
  useDailyEvent(
    'participant-updated',
    useCallback(
      (event: any) => {
        const participant = event?.participant;
        if (!participant || !isCallJoined) return; // Ignore events until call is fully joined
        
        const sessionId = participant.session_id;
        
        // Track video state for this participant
        const videoTrack = participant?.tracks?.video;
        const hasVideoOn = videoTrack?.state === 'playable' || 
                          (sessionId === localSessionId && videoTrack?.state === 'sendable' && !videoTrack?.off);
        
        setParticipantVideoState(prev => ({
          ...prev,
          [sessionId]: hasVideoOn,
        }));
        
        const screenVideoTrack = participant?.tracks?.screenVideo;
        const screenTrack = participant?.tracks?.screen;
        const screenVideoState = screenVideoTrack?.state;
        const screenState = screenTrack?.state;
        // Screen share is active only if track state is 'playable' (not 'sendable' which can be present before actual sharing)
        // For remote participants, only 'playable' means they're actually sharing
        // For local participant, check if track is subscribed/enabled
        const hasScreenTrack = 
          screenVideoState === 'playable' || 
          screenState === 'playable' ||
          (sessionId === localSessionId && (screenVideoState === 'sendable' || screenState === 'sendable'));
        
        if (sessionId === localSessionId) {
          // Local participant screen share
          if (hasScreenTrack && !isScreenSharing) {
            setIsScreenSharing(true);
            setScreenSharingSessionId(localSessionId);
          } else if (!hasScreenTrack && isScreenSharing && screenSharingSessionId === localSessionId) {
            setIsScreenSharing(false);
            setScreenSharingSessionId(null);
          }
        } else {
          // Remote participant screen share
          // Ensure participant is tracked in participantMap
          const participantName = participant.user_name || participantMap[sessionId]?.name || 'Provider';
          if (!participantMap[sessionId]) {
            setParticipantMap(prev => ({
              ...prev,
              [sessionId]: {
                sessionId: sessionId,
                name: participantName,
                isProvider: !isProvider, // Remote participant is opposite role
              },
            }));
          }
          
          if (hasScreenTrack && screenSharingSessionId !== sessionId) {
            // Remote participant started sharing
            setScreenSharingSessionId(sessionId);
            setIsScreenSharing(true);
          } else if (!hasScreenTrack && screenSharingSessionId === sessionId) {
            // This participant stopped sharing
            setScreenSharingSessionId(null);
            setIsScreenSharing(false);
          }
        }
      },
      [localSessionId, isScreenSharing, screenSharingSessionId, isCallJoined, participantMap, isProvider]
    )
  );

  // Handle incoming chat messages
  useDailyEvent(
    'app-message',
    useCallback(
      (event: any) => {
        if (event?.data?.type === 'chat') {
          const newMessage: ChatMessage = {
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            sender: event.fromId,
            senderName: event.data.senderName || 'Unknown',
            text: event.data.text,
            timestamp: new Date(),
            isLocal: false,
          };
          setChatMessages(prev => [...prev, newMessage]);
          
          // Increment unread count if chat panel is closed
          if (!showChatPanel) {
            setUnreadMessages(prev => prev + 1);
          }
          
          // Auto-scroll to bottom
          setTimeout(() => {
            if (chatScrollRef.current) {
              chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
            }
          }, 100);
        }
      },
      [showChatPanel]
    )
  );

  // Send chat message function
  const sendChatMessage = useCallback(() => {
    const messageText = chatInput.trim();
    if (!messageText) return;

    // Add to local messages first (works even if daily not connected)
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      sender: localSessionId || 'local',
      senderName: displayName,
      text: messageText,
      timestamp: new Date(),
      isLocal: true,
    };
    setChatMessages(prev => [...prev, newMessage]);
    setChatInput('');

    // Try to send to other participants if daily is connected
    if (daily) {
      try {
        const messageData = {
          type: 'chat',
          text: messageText,
          senderName: displayName,
        };
        daily.sendAppMessage(messageData, '*');
      } catch (error) {
        console.error('[VideoRoom] Failed to send chat message:', error);
      }
    }

    // Auto-scroll to bottom
    setTimeout(() => {
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      }
    }, 100);
  }, [daily, chatInput, displayName, localSessionId]);

  // Toggle chat panel - closes transcript sidebar when opening
  const toggleChat = useCallback(() => {
    setShowChatPanel(prev => {
      if (!prev) {
        // Opening chat - close transcript sidebar and reset unread count
        setShowTranscript(false);
        setUnreadMessages(0);
      }
      return !prev;
    });
  }, []);

  // Control functions
  const toggleAudio = useCallback(() => {
    if (!daily) return;
    daily.setLocalAudio(!isAudioOn);
    setIsAudioOn(!isAudioOn);
  }, [daily, isAudioOn]);

  const toggleVideo = useCallback(() => {
    if (!daily) return;
    daily.setLocalVideo(!isVideoOn);
    setIsVideoOn(!isVideoOn);
  }, [daily, isVideoOn]);

  const toggleScreenShare = useCallback(async () => {
    if (!daily || !isCallJoined) return; // Don't allow screen share until call is joined

    if (isScreenSharing) {
      try {
        await daily.stopScreenShare();
        setIsScreenSharing(false);
        setScreenSharingSessionId(null);
      } catch (error) {
        console.error('[VideoRoom] Failed to stop screen share:', error);
        toast({
          title: 'Screen Share Error',
          description: error instanceof Error ? error.message : 'Failed to stop screen sharing',
          variant: 'destructive',
        });
      }
    } else {
      try {
        await daily.startScreenShare();
        // State will be updated via participant-updated event
      } catch (error) {
        console.error('[VideoRoom] Failed to start screen share:', error);
        toast({
          title: 'Screen Share Error',
          description: error instanceof Error ? error.message : 'Failed to start screen sharing',
          variant: 'destructive',
        });
      }
    }
  }, [daily, isScreenSharing, toast, localSessionId, isCallJoined]);

  const leaveCall = useCallback(async () => {
    // Only provider saves the transcript
    if (daily && isProvider && clinicId && patientId && transcriptLines.length > 0) {
      try {
        console.log('[VideoRoom] Saving transcript with', transcriptLines.length, 'lines');
        const response = await transcriptService.saveTranscript(clinicId, {
          patient_id: patientId,
          appointment_id: appointmentId,
          encounter_id: encounterId,
          room_name: roomUrl?.split('/').pop(),
          transcript_lines: transcriptLines.map(line => ({
            speakerName: line.speakerName,
            speakerId: line.speakerId,
            text: line.text,
            timestamp: line.timestamp,
            isFinal: line.isFinal,
          })),
          duration_seconds: callDuration,
          clean_transcript: true, // Enable AI cleaning
        });
        console.log('[VideoRoom] Transcript saved:', response.transcript.id);
        setSavedTranscriptId(response.transcript.id); // Store for SOAP generation
        toast({
          title: 'Transcript Saved',
          description: `Transcript saved with ${response.transcript.word_count} words`,
        });
      } catch (error) {
        console.error('[VideoRoom] Failed to save transcript:', error);
        toast({
          title: 'Transcript Save Failed',
          description: 'Transcript could not be saved automatically',
          variant: 'destructive',
        });
      }
    }

    // Only room owner (provider) can stop transcription
    if (daily && isTranscribing && isProvider && isCallJoined) {
      try {
        const meetingState = daily.meetingState();
        if (meetingState === 'joined-meeting' || meetingState === 'joined') {
          await daily.stopTranscription().catch(console.error);
        }
      } catch (e) {
        // Ignore errors
      }
    }
    
    setIsCallJoined(false);
    
    // Leave Daily.co call if connected
    if (daily) {
      try {
        await daily.leave();
      } catch (e) {
        console.error('[VideoRoom] Error leaving call:', e);
        // Continue anyway to ensure onLeave is called
      }
    }
    
    // Always call onLeave to show ended screen, even if Daily.co wasn't initialized
    onLeave?.();
  }, [
    daily,
    onLeave,
    isTranscribing,
    isProvider,
    clinicId,
    patientId,
    appointmentId,
    encounterId,
    roomUrl,
    transcriptLines,
    callDuration,
    toast,
    isCallJoined,
  ]);

  const toggleTranscript = useCallback(() => {
    setShowTranscript(prev => {
      if (!prev) {
        // Opening transcript - close chat sidebar
        setShowChatPanel(false);
      }
      return !prev;
    });
  }, []);

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (joinError) {
    return (
      <div className='flex h-full flex-col items-center justify-center bg-[#0a0f14] p-8 text-white'>
        <img src='/nimbus_logo_white.svg' alt='Nimbbus Providers' className='mb-12 h-6' />
        <div className='flex h-24 w-24 items-center justify-center rounded-full bg-[#ea4335]/20'>
          <AlertCircle className='h-12 w-12 text-[#ea4335]' />
        </div>
        <h2 className='mt-8 text-2xl font-semibold'>Unable to Join Call</h2>
        <p className='mt-3 max-w-md text-center text-white/60'>{joinError}</p>
        <Button
          onClick={onLeave}
          className='mt-8 rounded-full bg-[#8ab4f8] px-6 py-2 font-medium text-[#202124] hover:bg-[#aecbfa]'
        >
          Go Back
        </Button>
      </div>
    );
  }

  if (isJoining) {
    return (
      <div className='flex h-full flex-col items-center justify-center bg-[#0a0f14] px-6 text-white'>
        <img src='/nimbus_logo_white.svg' alt='Nimbbus Providers' className='mb-10 h-6 md:mb-12' />
        <div className='relative'>
          <div className='h-14 w-14 rounded-full border-[3px] border-[#3c4043] md:h-20 md:w-20 md:border-4' />
          <div className='absolute inset-0 h-14 w-14 animate-spin rounded-full border-[3px] border-transparent border-t-brand-teal md:h-20 md:w-20 md:border-4' />
        </div>
        <p className='mt-6 text-base font-medium md:mt-8 md:text-xl'>Joining call...</p>
        <p className='mt-2 text-center text-sm text-white/60 md:mt-3 md:text-base'>Please allow camera and microphone access</p>
      </div>
    );
  }

  return (
    <div className='relative flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#0a0f14] md:h-full'>
      {/* Header - Responsive */}
      <div className='relative z-10 flex h-10 items-center justify-between border-b border-white/5 bg-[#0a0f14] px-2 md:h-12 md:px-4 lg:h-14 lg:px-5'>
        {/* Left: Logo and Live Badge */}
        <div className='flex items-center gap-2 md:gap-3 lg:gap-4'>
          <img src='/nimbus_logo_white.svg' alt='Nimbbus Providers' className='h-4 md:h-5' />
          <div className='hidden h-4 w-px bg-white/10 sm:block md:h-5' />
          <Badge 
            variant='outline' 
            className='h-5 border-green-500/50 bg-green-500/15 px-1.5 text-[10px] font-semibold text-green-400 md:h-6 md:px-2.5 md:text-xs'
          >
            <span className='mr-1 h-1.5 w-1.5 animate-pulse rounded-full bg-green-400 md:mr-1.5 md:h-2 md:w-2' />
            Live
          </Badge>
          </div>

        {/* Center: Call Info */}
        <div className='flex items-center gap-2 md:absolute md:left-1/2 md:-translate-x-1/2 md:gap-3 lg:gap-4'>
          <div className='flex items-center gap-1.5 rounded-full bg-white/5 px-2 py-1 md:gap-2 md:px-3 md:py-1.5'>
            <Clock className='h-3 w-3 text-white/60 md:h-4 md:w-4' />
            <span className='font-mono text-xs font-medium text-white md:text-sm'>{formatDuration(callDuration)}</span>
          </div>
          <div className='hidden items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 sm:flex'>
            <Users className='h-3.5 w-3.5 text-white/60 md:h-4 md:w-4' />
            <span className='text-xs font-medium text-white md:text-sm'>{participantIds.length + 1}</span>
          </div>
            <ConnectionQualityIndicator quality={connectionQuality} />
            {isReconnecting && (
            <div className='flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-1 text-[10px] font-medium text-amber-400 md:gap-1.5 md:px-3 md:py-1.5 md:text-xs'>
              <RefreshCw className='h-3 w-3 animate-spin md:h-3.5 md:w-3.5' />
              <span className='hidden sm:inline'>Reconnecting</span>
              </div>
            )}
          </div>

        {/* Right: Participant Name */}
        <div className='hidden items-center gap-3 md:flex'>
        {appointmentInfo && (
            <span className='text-xs font-medium text-white md:text-sm'>
            {isProvider ? appointmentInfo.patientName : appointmentInfo.providerName}
            </span>
        )}
        </div>
      </div>

      {/* Main Video Stage */}
      <div className='relative min-h-0 flex-1 overflow-hidden bg-[#0a0f14]'>
        {/* Video Area - controls overlay on mobile, padding on desktop */}
        <div 
          className={`absolute inset-0 h-full w-full p-2 pb-20 transition-all duration-300 md:p-3 md:pb-24 ${
            (showTranscript && isProvider && isTranscribing) || showChatPanel 
              ? 'md:pr-[320px] lg:pr-[360px]' 
              : ''
          }`}
        >
          {screenSharingSessionId ? (
            /* Screen Share Active Layout */
            <div className='flex h-full flex-col gap-2 md:gap-3'>
              {/* Main Screen Share */}
              <div className='relative flex-1 overflow-hidden rounded-xl bg-[#141c24] md:rounded-2xl'>
                  <DailyVideo
                    sessionId={screenSharingSessionId}
                    type='screenVideo'
                  className='h-full w-full rounded-xl object-contain md:rounded-2xl'
                />
                <div className='absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-blue-500/90 px-2 py-1 text-xs font-medium text-white shadow-lg md:left-4 md:top-4 md:gap-2 md:px-4 md:py-2 md:text-sm'>
                  <Monitor className='h-3.5 w-3.5 md:h-4 md:w-4' />
                  <span className='hidden sm:inline'>
                    {screenSharingSessionId === localSessionId 
                      ? 'You are presenting' 
                      : `${participantMap[screenSharingSessionId]?.name || 'Participant'} is presenting`}
                  </span>
                  <span className='sm:hidden'>Presenting</span>
                </div>
              </div>
              
              {/* Participant Strip */}
              <div className='flex h-20 gap-2 md:h-24 md:gap-3 lg:h-28'>
                {/* Remote participants first */}
                {participantIds.map((id, index) => (
                  <div
                    key={id}
                    className='relative aspect-video h-full overflow-hidden rounded-lg bg-[#141c24] md:rounded-xl'
                  >
                    {/* Avatar fallback */}
                    <div className='absolute inset-0 z-0 flex items-center justify-center bg-[#141c24]'>
                      <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#5f6368] md:h-10 md:w-10'>
                        <span className='text-xs font-medium text-white md:text-sm'>
                          {(participantMap[id]?.name || 'P').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {/* Only show DailyVideo when participant has video on */}
                    {participantVideoState[id] !== false && (
                      <DailyVideo sessionId={id} type='video' className='relative z-[1] h-full w-full rounded-lg object-cover md:rounded-xl' />
                    )}
                    <div className='absolute bottom-0 left-0 right-0 z-[2] bg-gradient-to-t from-black/70 to-transparent p-1.5 md:p-2'>
                      <span className='text-[10px] font-medium text-white md:text-xs'>
                        {participantMap[id]?.name || (isProvider ? appointmentInfo?.patientName : appointmentInfo?.providerName) || 'Participant'}
                      </span>
                    </div>
                    {index === 0 && (
                      <div className='absolute right-1.5 top-1.5 z-[3] h-2 w-2 rounded-full bg-amber-400 shadow-lg shadow-amber-400/50 md:right-2 md:top-2 md:h-2.5 md:w-2.5' />
                    )}
                  </div>
                ))}
                {/* Self view in strip */}
                {localSessionId && (
                  <div className='relative aspect-video h-full overflow-hidden rounded-lg bg-[#141c24] md:rounded-xl'>
                    {/* Avatar fallback for self */}
                    {!isVideoOn && (
                      <div className='absolute inset-0 z-0 flex items-center justify-center bg-[#141c24]'>
                        <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#5f6368] md:h-10 md:w-10'>
                          <VideoOff className='h-4 w-4 text-white/70 md:h-5 md:w-5' />
                        </div>
                      </div>
                    )}
                    {isVideoOn && (
                      <DailyVideo
                        sessionId={localSessionId}
                        mirror
                        type='video'
                        className='h-full w-full rounded-lg object-cover md:rounded-xl'
                      />
                    )}
                    <div className='absolute bottom-0 left-0 right-0 z-[2] bg-gradient-to-t from-black/70 to-transparent p-1.5 md:p-2'>
                      <span className='text-[10px] font-medium text-white md:text-xs'>You</span>
                    </div>
                      </div>
                    )}
              </div>
            </div>
          ) : participantIds.length === 0 ? (
            /* Waiting for Participant - Self View Large */
            <div className='relative h-full'>
              {/* Large Self View */}
              <div className='relative h-full overflow-hidden rounded-xl bg-[#141c24] md:rounded-2xl'>
                {localSessionId && (
                  <DailyVideo
                    sessionId={localSessionId}
                    mirror
                    type='video'
                    className='h-full w-full rounded-xl object-cover md:rounded-2xl'
                  />
                )}
                {/* Name Label - top-right on mobile, bottom on desktop */}
                <div className='absolute right-2 top-2 z-10 rounded-lg bg-black/60 px-2 py-1 backdrop-blur-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:rounded-none md:bg-gradient-to-t md:from-black/80 md:via-black/40 md:to-transparent md:p-4 md:backdrop-blur-none lg:p-6'>
                  <span className='text-xs font-medium text-white md:text-base'>You {!isVideoOn && '(Camera Off)'}</span>
                </div>
                {!isAudioOn && (
                  <div className='absolute right-2 top-2 flex items-center gap-1.5 rounded-full bg-red-500 px-2 py-1 text-[10px] font-medium text-white shadow-lg md:right-4 md:top-4 md:gap-2 md:px-3 md:py-1.5 md:text-xs'>
                    <MicOff className='h-3 w-3 md:h-3.5 md:w-3.5' />
                    <span>Muted</span>
                      </div>
                    )}
              </div>
              
              {/* Waiting Overlay */}
              <div className='absolute inset-0 flex items-center justify-center overflow-hidden rounded-xl bg-black/50 md:rounded-2xl'>
                <div className='flex flex-col items-center rounded-xl bg-[#0a0f14]/90 px-6 py-5 backdrop-blur-sm md:rounded-2xl md:px-10 md:py-7 lg:px-12 lg:py-8'>
                  <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal md:mb-4 md:h-14 md:w-14 lg:mb-5 lg:h-16 lg:w-16'>
                    <Users className='h-6 w-6 text-white md:h-7 md:w-7 lg:h-8 lg:w-8' strokeWidth={1.5} />
                  </div>
                  <p className='text-base font-medium text-white md:text-lg lg:text-xl'>
                    Waiting for {isProvider ? 'patient' : 'provider'}
                  </p>
                  <p className='mt-1.5 text-xs text-white/70 md:mt-2 md:text-sm'>
                    They'll appear here when they connect
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Speaker View - Remote participant large, self PiP (with swap on click) */
            <div className='relative h-full'>
              {/* Main Video View - Shows remote or self based on swap state */}
              <div 
                className='absolute inset-0 cursor-pointer overflow-hidden rounded-xl bg-[#141c24] md:rounded-2xl'
                onClick={() => setIsViewSwapped(!isViewSwapped)}
              >
                {isViewSwapped ? (
                  /* Swapped: Self view as main */
                  <>
                    {localSessionId && (
                      <>
                        <DailyVideo
                          sessionId={localSessionId}
                          mirror
                          type='video'
                          className='h-full w-full rounded-xl object-cover md:rounded-2xl'
                        />
                        {!isVideoOn && (
                          <div className='absolute inset-0 flex items-center justify-center bg-[#141c24]'>
                            <div className='flex h-24 w-24 items-center justify-center rounded-full bg-[#5f6368] md:h-32 md:w-32'>
                              <span className='text-3xl font-medium text-white md:text-4xl'>You</span>
                            </div>
                          </div>
                        )}
                        <div className='absolute right-2 top-2 z-10 rounded-lg bg-black/60 px-2 py-1 backdrop-blur-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:rounded-none md:bg-gradient-to-t md:from-black/80 md:via-black/40 md:to-transparent md:p-4 md:backdrop-blur-none lg:p-6'>
                          <span className='text-xs font-medium text-white md:text-base lg:text-lg'>You {!isVideoOn && '(Camera Off)'}</span>
                        </div>
                        {!isAudioOn && (
                          <div className='absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-red-500 px-2 py-1 text-[10px] font-medium text-white shadow-lg md:right-4 md:top-4 md:gap-2 md:px-3 md:py-1.5 md:text-xs'>
                            <MicOff className='h-3 w-3 md:h-3.5 md:w-3.5' />
                            <span>Muted</span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  /* Normal: Remote participant as main */
                  <>
                {participantIds[0] ? (
                  <>
                    {/* Avatar fallback - always shown behind video */}
                    <div className='absolute inset-0 z-0 flex items-center justify-center bg-[#141c24]'>
                      <div className='flex h-24 w-24 items-center justify-center rounded-full bg-[#5f6368] md:h-32 md:w-32'>
                        <span className='text-3xl font-medium text-white md:text-4xl'>
                          {(participantMap[participantIds[0]]?.name || (isProvider ? appointmentInfo?.patientName : appointmentInfo?.providerName) || 'P')
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {/* Only show DailyVideo when participant has video on */}
                    {participantVideoState[participantIds[0]] !== false && (
                      <DailyVideo 
                        sessionId={participantIds[0]} 
                        type='video' 
                        className='absolute inset-0 z-[1] h-full w-full rounded-xl object-cover md:rounded-2xl' 
                      />
                    )}
                    <div className='absolute left-2 top-2 z-10 flex items-center gap-2 md:left-4 md:top-4 md:gap-3'>
                      <div className='flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-1 backdrop-blur-sm md:gap-2 md:px-4 md:py-2'>
                        <div className='h-2 w-2 animate-pulse rounded-full bg-amber-400 shadow-lg shadow-amber-400/50 md:h-2.5 md:w-2.5' />
                        <span className='text-xs font-medium text-white md:text-sm'>Active Speaker</span>
                      </div>
                    </div>
                    <div className='absolute right-2 top-10 z-10 rounded-lg bg-black/60 px-2 py-1 backdrop-blur-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:rounded-none md:bg-gradient-to-t md:from-black/80 md:via-black/40 md:to-transparent md:p-4 md:backdrop-blur-none lg:p-6'>
                      <span className='text-xs font-medium text-white md:text-base lg:text-lg'>
                        {participantMap[participantIds[0]]?.name || (isProvider ? appointmentInfo?.patientName : appointmentInfo?.providerName) || 'Participant'}
                        {participantVideoState[participantIds[0]] === false && ' (Camera Off)'}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className='flex h-full items-center justify-center'>
                    <span className='text-white/50'>No video</span>
                  </div>
                    )}
                  </>
                )}
              </div>

              {/* Additional participants (if more than 1) - shown as thumbnails on top right */}
              {participantIds.length > 1 && (
                <div className='absolute right-2 top-2 z-20 flex flex-col gap-2 md:right-4 md:top-4 md:gap-3'>
                  {participantIds.slice(1).map((id) => (
                    <div
                      key={id}
                      className='relative h-20 w-28 overflow-hidden rounded-lg bg-[#141c24] shadow-2xl ring-1 ring-white/10 md:h-24 md:w-36 md:rounded-xl md:ring-2 lg:h-28 lg:w-40'
                    >
                      <div className='absolute inset-0 z-0 flex items-center justify-center bg-[#141c24]'>
                        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-[#5f6368] md:h-12 md:w-12'>
                          <span className='text-sm font-medium text-white md:text-base'>
                            {(participantMap[id]?.name || 'P').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      {/* Only show DailyVideo when participant has video on */}
                      {participantVideoState[id] !== false && (
                        <DailyVideo sessionId={id} type='video' className='relative z-[1] h-full w-full rounded-lg object-cover md:rounded-xl' />
                      )}
                      <div className='absolute bottom-0 left-0 right-0 z-[2] bg-gradient-to-t from-black/70 to-transparent p-1.5 md:p-2'>
                        <span className='text-[10px] font-medium text-white md:text-xs'>
                          {participantMap[id]?.name || 'Participant'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* PiP View - Shows opposite of main view (clickable to swap) */}
              <div 
                className='group absolute bottom-16 right-2 z-20 h-[90px] w-[120px] cursor-pointer overflow-hidden rounded-xl bg-[#141c24] shadow-2xl ring-2 ring-white/20 transition-all duration-300 hover:scale-105 hover:ring-white/40 active:scale-95 md:bottom-4 md:right-4 md:h-[120px] md:w-[170px] md:rounded-2xl lg:h-[140px] lg:w-[200px]'
                onClick={() => setIsViewSwapped(!isViewSwapped)}
              >
                {isViewSwapped ? (
                  /* Swapped: Remote in PiP */
                  <>
                    {participantIds[0] && (
                      <>
                        <div className='absolute inset-0 z-0 flex items-center justify-center bg-[#141c24]'>
                          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#5f6368] md:h-10 md:w-10'>
                            <span className='text-sm font-medium text-white md:text-base'>
                              {(participantMap[participantIds[0]]?.name || 'P').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        {/* Only show DailyVideo when participant has video on */}
                        {participantVideoState[participantIds[0]] !== false && (
                          <DailyVideo 
                            sessionId={participantIds[0]} 
                            type='video' 
                            className='relative z-[1] h-full w-full rounded-xl object-cover md:rounded-2xl' 
                          />
                        )}
                        <div className='absolute left-1 top-1 z-[2] rounded bg-black/60 px-1.5 py-0.5 backdrop-blur-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:rounded-none md:bg-gradient-to-t md:from-black/80 md:to-transparent md:px-3 md:py-2 md:backdrop-blur-none'>
                          <span className='line-clamp-1 max-w-[80px] text-[10px] font-medium text-white md:max-w-none md:text-sm'>
                            {participantMap[participantIds[0]]?.name || (isProvider ? appointmentInfo?.patientName : appointmentInfo?.providerName) || 'Participant'}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* Normal: Self in PiP */
                  <>
              {localSessionId && (
                      <>
                  <DailyVideo
                    sessionId={localSessionId}
                    mirror
                    type='video'
                    className='h-full w-full rounded-xl object-cover md:rounded-2xl'
                  />
                  <div className='absolute right-1 top-1 z-10 rounded bg-black/60 px-1.5 py-0.5 backdrop-blur-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:rounded-none md:bg-gradient-to-t md:from-black/80 md:to-transparent md:px-3 md:py-2 md:backdrop-blur-none'>
                    <span className='text-[10px] font-medium text-white md:text-sm'>You</span>
                  </div>
                  {!isAudioOn && (
                    <div className='absolute right-1.5 top-1.5 rounded-full bg-red-500 p-1 shadow-lg md:right-2 md:top-2 md:p-1.5'>
                      <MicOff className='h-2.5 w-2.5 text-white md:h-3 md:w-3' />
                    </div>
                  )}
                  {!isVideoOn && (
                    <div className='absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-[#141c24] md:rounded-2xl'>
                      <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#5f6368] md:h-10 md:w-10'>
                        <VideoOff className='h-4 w-4 text-white/70 md:h-5 md:w-5' />
                      </div>
                    </div>
                  )}
                      </>
                    )}
                  </>
                )}
                {/* Tap hint on hover */}
                <div className='pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 md:rounded-2xl'>
                  <span className='text-[10px] font-medium text-white md:text-xs'>Tap to switch</span>
                </div>
              </div>
                    </div>
              )}
            </div>

        {/* Side Panel - Transcript & Summary (for providers) */}
        {/* Mobile: full overlay, Desktop: side panel */}
        {showTranscript && isProvider && isTranscribing && (
          <div 
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={() => setShowTranscript(false)}
          />
        )}
        <div 
          className={`fixed inset-x-0 bottom-0 top-14 z-50 transform overflow-hidden transition-transform duration-300 ease-out md:absolute md:inset-x-auto md:bottom-24 md:right-0 md:top-3 md:z-30 md:w-[320px] md:rounded-l-xl md:border-l md:border-y md:border-white/10 lg:w-[360px] ${
            showTranscript && isProvider && isTranscribing ? 'translate-y-0 md:translate-x-0 md:translate-y-0' : 'translate-y-full md:translate-x-full md:translate-y-0'
          }`}
        >
          <div className='flex h-full flex-col bg-[#0a0f14]'>
            {/* Tab Header */}
            <div className='flex items-center border-b border-white/10'>
              <button
                onClick={() => setActiveSideTab('summary')}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-all md:gap-2 md:px-4 md:py-3 md:text-sm ${
                  activeSideTab === 'summary'
                    ? 'border-b-2 border-purple-400 bg-purple-500/10 text-purple-400'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Loader2 className='h-3.5 w-3.5 md:h-4 md:w-4' />
                <span className='hidden sm:inline'>AI</span> Summary
              </button>
              <button
                onClick={() => setActiveSideTab('transcript')}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-all md:gap-2 md:px-4 md:py-3 md:text-sm ${
                  activeSideTab === 'transcript'
                    ? 'border-b-2 border-green-400 bg-green-500/10 text-green-400'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <FileText className='h-3.5 w-3.5 md:h-4 md:w-4' />
                Transcript
                {isTranscribing && (
                  <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-red-400' />
                )}
              </button>
              {/* Close button for mobile */}
              <button
                onClick={() => setShowTranscript(false)}
                className='flex h-10 w-10 items-center justify-center text-white/60 hover:bg-white/10 hover:text-white md:hidden'
              >
                <X className='h-5 w-5' />
              </button>
        </div>

            {/* Tab Content */}
            <div className='min-h-0 flex-1 overflow-hidden'>
              {activeSideTab === 'summary' ? (
              <LiveSummaryPanel
                clinicId={clinicId || ''}
                patientName={appointmentInfo?.patientName}
                transcriptLines={transcriptLines}
                isTranscribing={isTranscribing}
              />
              ) : (
              <TranscriptPanel
                lines={transcriptLines}
                isActive={isTranscribing}
                onClose={toggleTranscript}
              />
              )}
            </div>
          </div>
      </div>

        {/* Separate Chat Panel */}
        {/* Mobile: full overlay, Desktop: side panel */}
        {showChatPanel && (
          <div 
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={() => setShowChatPanel(false)}
          />
        )}
        <div 
          className={`fixed inset-x-0 bottom-0 top-14 z-50 transform overflow-hidden transition-transform duration-300 ease-out md:absolute md:inset-x-auto md:bottom-24 md:right-0 md:top-3 md:z-30 md:w-[320px] md:rounded-l-xl md:border-l md:border-y md:border-white/10 lg:w-[360px] ${
            showChatPanel ? 'translate-y-0 md:translate-x-0 md:translate-y-0' : 'translate-y-full md:translate-x-full md:translate-y-0'
          }`}
        >
          <div className='flex h-full flex-col bg-[#0a0f14]'>
            {/* Chat Header */}
            <div className='flex items-center justify-between border-b border-white/10 px-4 py-3'>
              <div className='flex items-center gap-2'>
                <MessageCircle className='h-4 w-4 text-[#8ab4f8]' />
                <span className='text-sm font-medium text-white'>In-call messages</span>
              </div>
              <button
                onClick={() => setShowChatPanel(false)}
                className='rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white'
              >
                <X className='h-4 w-4' />
              </button>
            </div>

            {/* Chat Messages */}
            <div
              ref={chatScrollRef}
              className='flex-1 overflow-y-auto p-3 md:p-4'
            >
              {chatMessages.length === 0 ? (
                <div className='flex h-full flex-col items-center justify-center text-center'>
                  <MessageCircle className='mb-2 h-8 w-8 text-white/20' />
                  <p className='text-sm text-white/40'>No messages yet</p>
                  <p className='mt-1 text-xs text-white/30'>Messages are only visible to participants in this call</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {chatMessages.map((msg) => {
                    return (
                      <div key={msg.id} className={`flex ${msg.isLocal ? 'justify-end' : 'justify-start'}`}>
                        {/* Message Bubble */}
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${msg.isLocal ? 'bg-[#8ab4f8]/20' : 'bg-[#2b2c2f]'}`}>
                          {/* Message Text */}
                          <p className='whitespace-pre-wrap break-words text-[13px] leading-relaxed text-white/90'>
                            {msg.text}
                          </p>
                          {/* Timestamp */}
                          <div className='mt-1 flex justify-end'>
                            <span className='text-[10px] text-white/40'>
                              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
          </div>
        )}
      </div>

            {/* Chat Input */}
            <div className='border-t border-white/10 p-3'>
              <div className='flex items-end gap-2'>
                <input
                  type='text'
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      sendChatMessage();
                    }
                  }}
                  placeholder='Type a message...'
                  className='chat-input-dark h-10 flex-1 rounded-full border-0 px-4 text-base outline-none focus:outline-none focus:ring-0 md:text-sm'
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim()}
                  className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#8ab4f8] text-[#202124] transition-all hover:bg-[#aecbfa] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <Send className='h-4 w-4' />
                </button>
              </div>
              <p className='mt-2 text-[10px] text-white/40'>
                Press Enter to send
              </p>
            </div>
          </div>
      </div>

        {/* Floating Control Bar - Google Meet Style - Responsive */}
        {/* Mobile: overlay on video with slight transparency, Desktop: solid background */}
        <div className='absolute bottom-2 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#141c24]/90 backdrop-blur-sm px-2.5 py-2.5 shadow-2xl md:bottom-6 md:bg-[#141c24] md:backdrop-blur-none md:gap-2 md:px-3 md:py-2.5 lg:gap-3 lg:px-4 lg:py-3'>
          {/* Primary Controls Group */}
          <div className='flex items-center gap-1 md:gap-1.5 lg:gap-2'>
            {/* Mic Button */}
            <button
              onClick={toggleAudio}
              className={`group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 md:h-11 md:w-11 lg:h-12 lg:w-12 ${
            isAudioOn
                  ? 'bg-[#5f6368] text-white hover:bg-[#80868b]'
                  : 'bg-white text-[#ea4335] hover:bg-gray-100'
              }`}
              title={isAudioOn ? 'Turn off microphone' : 'Turn on microphone'}
            >
              {isAudioOn ? <Mic className='h-4 w-4 md:h-5 md:w-5' /> : <MicOff className='h-4 w-4 md:h-5 md:w-5' />}
              <span className='pointer-events-none absolute -top-10 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#0a0f14] px-2 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 md:block'>
                {isAudioOn ? 'Mute' : 'Unmute'}
              </span>
            </button>

            {/* Video Button */}
            <button
              onClick={toggleVideo}
              className={`group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 md:h-11 md:w-11 lg:h-12 lg:w-12 ${
            isVideoOn
                  ? 'bg-[#5f6368] text-white hover:bg-[#80868b]'
                  : 'bg-white text-[#ea4335] hover:bg-gray-100'
              }`}
              title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
            >
              {isVideoOn ? <Video className='h-4 w-4 md:h-5 md:w-5' /> : <VideoOff className='h-4 w-4 md:h-5 md:w-5' />}
              <span className='pointer-events-none absolute -top-10 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#0a0f14] px-2 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 md:block'>
                {isVideoOn ? 'Stop video' : 'Start video'}
              </span>
            </button>
          </div>

          {/* Divider */}
          <div className='h-6 w-px bg-white/20 md:h-7 lg:h-8' />

          {/* Secondary Controls Group */}
          <div className='flex items-center gap-1 md:gap-1.5 lg:gap-2'>
        {isProvider && (
          <>
                {/* Screen Share */}
                <button
              onClick={toggleScreenShare}
              disabled={!isCallJoined}
                  className={`group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 md:h-11 md:w-11 lg:h-12 lg:w-12 ${
                    isScreenSharing
                      ? 'bg-[#a5b4fc] text-[#3730a3] hover:bg-[#c7d2fe]'
                      : 'bg-[#5f6368] text-white hover:bg-[#80868b] disabled:cursor-not-allowed disabled:opacity-40'
                  }`}
                  title={isScreenSharing ? 'Stop presenting' : 'Present now'}
                >
                  {isScreenSharing ? <MonitorUp className='h-4 w-4 md:h-5 md:w-5' /> : <Monitor className='h-4 w-4 md:h-5 md:w-5' />}
                </button>

                {/* Transcript Toggle */}
            {isTranscribing && (
                  <button
                    onClick={toggleTranscript}
                    className={`group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 md:h-11 md:w-11 lg:h-12 lg:w-12 ${
                  showTranscript
                        ? 'bg-[#81c995] text-[#202124] hover:bg-[#a8dab5]'
                        : 'bg-[#5f6368] text-white hover:bg-[#80868b]'
                }`}
                    title={showTranscript ? 'Hide captions' : 'Show captions'}
              >
                    <FileText className='h-4 w-4 md:h-5 md:w-5' />
                  </button>
            )}

                {/* SOAP Notes - Hidden on small screens */}
            {transcriptLines.length > 0 && patientId && clinicId && (
                  <button
                    onClick={() => setShowSOAPEditor(true)}
                    className={`group relative hidden h-11 w-11 items-center justify-center rounded-full transition-all duration-200 sm:flex md:h-11 md:w-11 lg:h-12 lg:w-12 ${
                  showSOAPEditor
                        ? 'bg-[#c58af9] text-[#202124] hover:bg-[#d7aefb]'
                        : 'bg-[#5f6368] text-white hover:bg-[#80868b]'
                }`}
                title='Generate SOAP Notes'
              >
                    <ClipboardCheck className='h-4 w-4 md:h-5 md:w-5' />
                  </button>
            )}
          </>
        )}

            {/* Chat */}
            <button
              onClick={toggleChat}
              className={`group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 md:h-11 md:w-11 lg:h-12 lg:w-12 ${
                showChatPanel
                  ? 'bg-[#8ab4f8] text-[#202124] hover:bg-[#aecbfa]'
                  : 'bg-[#5f6368] text-white hover:bg-[#80868b]'
              }`}
              title='Chat'
            >
              <MessageCircle className='h-4 w-4 md:h-5 md:w-5' />
              {unreadMessages > 0 && !showChatPanel && (
                <span className='absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white'>
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </button>

            {/* Settings */}
            <button
          onClick={() => setShowDeviceSettings(true)}
              className='group relative flex h-11 w-11 items-center justify-center rounded-full bg-[#5f6368] text-white transition-all duration-200 hover:bg-[#80868b] md:h-11 md:w-11 lg:h-12 lg:w-12'
              title='Settings'
            >
              <Settings className='h-4 w-4 md:h-5 md:w-5' />
            </button>
          </div>

          {/* Divider */}
          <div className='h-6 w-px bg-white/20 md:h-7 lg:h-8' />

          {/* End Call Button */}
          <button
          onClick={leaveCall}
            className='group relative flex h-11 w-11 items-center justify-center rounded-full bg-[#ea4335] text-white transition-all duration-200 hover:bg-[#d93025] md:h-11 md:w-11 lg:h-12 lg:w-12'
            title='Leave call'
        >
            <PhoneOff className='h-4 w-4 md:h-5 md:w-5' />
          </button>
        </div>
      </div>

      {/* Audio elements */}
      <DailyAudio />


      {/* Device Settings Panel */}
      <DeviceSettingsPanel
        isOpen={showDeviceSettings}
        onOpenChange={setShowDeviceSettings}
        triggerButton={false}
      />

      {/* SOAP Note Editor Dialog */}
      {isProvider && patientId && clinicId && (
        <TranscriptSOAPEditor
          transcriptLines={transcriptLines}
          transcriptId={savedTranscriptId}
          patientId={patientId}
          patientName={appointmentInfo?.patientName}
          appointmentId={appointmentId}
          encounterId={encounterId}
          clinicId={clinicId}
          isOpen={showSOAPEditor}
          onClose={() => setShowSOAPEditor(false)}
          onSave={note => {
            toast({
              title: 'SOAP Note Saved',
              description: 'Clinical documentation has been saved to the patient record.',
            });
            setShowSOAPEditor(false);
          }}
        />
      )}
    </div>
  );
};

// Main component with DailyProvider wrapper
const VideoRoom: React.FC<VideoRoomProps> = props => {
  const callObject = useCallObject({
    options: {
      // Use default audio/video devices
      audioSource: true,
      videoSource: true,
    }
  });

  if (!callObject) {
    return (
      <div className='flex h-full flex-col items-center justify-center bg-[#0a0f14]'>
        <img src='/nimbus_logo_white.svg' alt='Nimbbus Providers' className='mb-12 h-6' />
        <div className='relative'>
          <div className='h-20 w-20 rounded-full border-4 border-[#3c4043]' />
          <div className='absolute inset-0 h-20 w-20 animate-spin rounded-full border-4 border-transparent border-t-[#8ab4f8]' />
        </div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <VideoRoomInner {...props} />
    </DailyProvider>
  );
};

export default VideoRoom;
